/**
 * Service for breeding-specific business logic
 */

import { StrainProgression } from '../types/breeding';

/**
 * Generate self-pollination strain name progression
 *
 * This function handles the automatic incrementing of strain names for self-pollination:
 * - "Honey Runtz" -> "Honey Runtz S1"
 * - "Honey Runtz S1" -> "Honey Runtz S2"
 * - "Honey Runtz S2" -> "Honey Runtz S3"
 * - "Honey Runtz F1" -> "Honey Runtz S1" (F-generation stripped)
 * - "Honey Runtz F2" -> "Honey Runtz S1" (F-generation stripped)
 * etc.
 *
 * @param currentName - Current strain name
 * @returns Strain progression object with new name
 */
export function generateSelfPollinationStrainName(currentName: string): StrainProgression {
  const trimmedName = currentName.trim();

  // Pattern to match strain names ending with " S<number>"
  // Examples: "Honey Runtz S1", "Blue Dream S10"
  const sNumberPattern = /^(.+?)\s+S(\d+)$/i;
  const sMatch = trimmedName.match(sNumberPattern);

  if (sMatch) {
    // Strain already has S-number, increment it
    const baseName = sMatch[1].trim();
    const currentGeneration = parseInt(sMatch[2], 10);
    const newGeneration = currentGeneration + 1;
    const newName = `${baseName} S${newGeneration}`;

    return {
      originalName: trimmedName,
      newName,
      generation: newGeneration,
      baseName
    };
  }

  // Pattern to match strain names ending with " F<number>"
  // Examples: "Honey Runtz F1", "Blue Dream F2"
  // These should be stripped before adding S1
  const fNumberPattern = /^(.+?)\s+F(\d+)$/i;
  const fMatch = trimmedName.match(fNumberPattern);

  if (fMatch) {
    // Strain has F-number, strip it and add S1
    const baseName = fMatch[1].trim();
    const newName = `${baseName} S1`;

    return {
      originalName: trimmedName,
      newName,
      generation: 1,
      baseName
    };
  }

  // No S-number or F-number, this is the first self-pollination
  const newName = `${trimmedName} S1`;

  return {
    originalName: trimmedName,
    newName,
    generation: 1,
    baseName: trimmedName
  };
}

/**
 * Generate a first-generation hybrid strain name from seed and pollen parents.
 *
 * The name is deliberately separate from self-pollination S-number progression:
 * F-generations are not stripped here because the parent labels are evidence of
 * the cross that produced this batch.
 */
export function generateF1HybridStrainName(
  seedParentName: string,
  pollenDonorName: string
): StrainProgression {
  const seedParent = seedParentName.trim().replace(/\s+/g, ' ');
  const pollenDonor = pollenDonorName.trim().replace(/\s+/g, ' ');
  const baseName = `${seedParent} x ${pollenDonor}`;

  return {
    originalName: baseName,
    newName: `${baseName} F1`,
    generation: 1,
    baseName
  };
}

/**
 * Validate that a plant can be used in the breeding workflow
 *
 * @param plant - Plant to validate
 * @returns Validation result with error message if invalid
 */
export function validatePlantForBreeding(plant: any): { valid: boolean; error?: string } {
  // Check if plant is destroyed
  if (plant.IsDestroyed) {
    return {
      valid: false,
      error: 'Plant has been destroyed and cannot be used for breeding'
    };
  }

  // Check if plant is deleted
  if (plant.IsDeleted) {
    return {
      valid: false,
      error: 'Plant has been deleted and cannot be used for breeding'
    };
  }

  // Check if plant is harvested
  if (plant.GrowPhase === 'Destroyed' || plant.GrowPhase === 'Harvested') {
    return {
      valid: false,
      error: `Plant is in ${plant.GrowPhase} phase and cannot be used for breeding`
    };
  }

  return { valid: true };
}

/**
 * Format plant display for user selection
 *
 * @param plant - Plant to format
 * @param index - Display index (1-based)
 * @returns Formatted string for display
 */
export function formatPlantDisplay(plant: any, index: number): string {
  const motherStatus = plant.IsMother ? ' [MOTHER]' : '';
  return `${index}. ${plant.TSID} - ${plant.StrainName}${motherStatus} (Room: ${plant.RoomName})`;
}
